import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  ArrowForwardRounded,
  CheckCircleRounded,
  RadioButtonUncheckedRounded,
  RefreshRounded,
  WarningAmberRounded,
} from '@mui/icons-material';
import type { AcademicLevel, CapCode, CapJourneyStep, CapJourneyStepKey } from '@dosfilos/domain';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useTenantStore } from '../../stores/tenant.store';
import { useCapProgress } from '../../hooks/use-cap-progress';
import { formatDate } from '../../lib/i18n-format';
import { useCapNetworkReview } from './components/CapNetworkReviewPanel';
import { CapPageHeader, CAP_CONTENT_MAX_WIDTH } from './components/CapPageHeader';

// ─── CAP journey (Phase E) — per-program guided certification hub ─────────────
// The front door to CAP for a program: one measured checklist (derived from the
// compliance audit) that walks the coordinator CCDs → PLOs → matrix → audit →
// proposal, deep-linking each step to the page that completes it.
//
// Layout: a two-column shell — a sticky summary rail (progress + audit status +
// certification config) beside the ordered step list — so the page fills the
// content container instead of a narrow centered column.

const CAP_CODES: CapCode[] = ['BSC', 'DEP'];
const LEVELS: AcademicLevel[] = ['undergraduate', 'graduate'];

type StepTone = 'success' | 'warning' | 'next' | 'pending';

const TONE_ACCENT: Record<StepTone, string> = {
  success: 'success.main',
  warning: 'warning.main',
  next: 'primary.main',
  pending: 'divider',
};

export function CapJourneyPage(): JSX.Element {
  const { t } = useTranslation('cap');
  const navigate = useNavigate();
  const { programId } = useParams<{ programId: string }>();
  const { currentTenantId } = useTenantStore();

  const [capCode, setCapCode] = useState<CapCode>('BSC');
  const [level, setLevel] = useState<AcademicLevel>('undergraduate');
  const [programName, setProgramName] = useState('');
  /** Si el programa declara un CAP objetivo. Sin él, el selector arranca en BSC
   *  y el Camino se lee como si ese programa persiguiera el Certificado — que es
   *  justo el peldaño anterior, así que el error es creíble y pasa inadvertido. */
  const [hasCapTarget, setHasCapTarget] = useState<boolean | null>(null);

  useEffect(() => {
    if (!currentTenantId || !programId) return;
    let alive = true;
    void getDoc(doc(db, `tenants/${currentTenantId}/programs/${programId}`)).then((snap) => {
      if (!alive || !snap.exists()) return;
      const d = snap.data();
      setProgramName((d['name'] as string) || '');
      // Pre-select the CAP + level the program targets (1:1), so the journey opens
      // on the right certification instead of the BSC default. Still switchable.
      const ct = d['capTarget'] as { capCode?: CapCode; level?: AcademicLevel } | undefined;
      setHasCapTarget(!!ct?.capCode);
      if (ct?.capCode) setCapCode(ct.capCode);
      if (ct?.level) setLevel(ct.level);
    });
    return () => {
      alive = false;
    };
  }, [currentTenantId, programId]);

  // La respuesta de TMAI: la misma que lee la pantalla de la propuesta.
  const review = useCapNetworkReview(currentTenantId, programId, capCode, level);
  const { loading, error, progress, latestSnapshot, refresh } = useCapProgress(
    programId,
    capCode,
    level,
  );

  const withGaps = latestSnapshot?.overallStatus === 'with_gaps';
  const compliant = latestSnapshot?.overallStatus === 'compliant';

  // Deep-link each step to the page that completes it.
  const hrefFor = (key: CapJourneyStepKey): string => {
    switch (key) {
      case 'ccds':
        // La ficha es del CURSO: el paso lleva a la malla del programa, donde
        // cada curso muestra si le falta y se abre con un clic. Antes llevaba a
        // la lista de plantillas de syllabus, que era el único camino cuando la
        // ficha colgaba de la plantilla.
        return `/academic/programs/${programId}`;
      case 'plos':
        return `/cap/${programId}/plos`;
      case 'matrix':
        return `/cap/${programId}/matriz`;
      case 'audit':
        return `/cap/${programId}/cumplimiento`;
      case 'proposal':
        return `/cap/${programId}/propuesta`;
    }
  };

  // A step can be "done but with gaps" — the audit is complete (snapshot saved)
  // yet the program doesn't meet the standard. That must not read as clean-green.
  const toneFor = (step: CapJourneyStep, isNext: boolean): StepTone => {
    if (step.key === 'audit' && step.done && withGaps) return 'warning';
    if (step.done) return 'success';
    if (isNext) return 'next';
    return 'pending';
  };

  // Localized detail line per step, from the domain's numeric signals.
  const detailFor = (step: CapJourneyStep): string => {
    const { count = 0, total = 0, status } = step.detail;
    switch (step.key) {
      case 'ccds':
        return step.done
          ? t('journey.steps.ccds.detailDone')
          : t('journey.steps.ccds.detailPending', { count });
      case 'plos':
        return step.done
          ? t('journey.steps.plos.detailDone', { count })
          : t('journey.steps.plos.detailPending');
      case 'matrix':
        return t('journey.steps.matrix.detail', { count, total });
      case 'audit':
        return step.done
          ? t('journey.steps.audit.detailDone', {
              status: status ? t(`compliance.status.${status}`) : '',
            })
          : count > 0
            ? t('journey.steps.audit.detailGap', { count })
            : t('journey.steps.audit.detailPending');
      case 'proposal':
        return status && status !== 'none'
          ? t('journey.steps.proposal.detailStatus', { status: t(`proposal.status.${status}`) })
          : t('journey.steps.proposal.detailPending');
    }
  };

  // TMAI ya decidió: el Camino mide preparación para SOMETER, y una vez que la
  // ronda terminó deja de tener sentido empujar al paso siguiente. Un programa
  // certificado seguía mostrando «4 de 5 pasos» con la matriz marcada como
  // «Siguiente» — pedirle más trabajo a quien ya fue aprobado le dice que algo
  // salió mal cuando salió bien.
  const certification = review.certification;
  const certified = certification?.status === 'granted';
  const nextHref = certified || !progress?.nextStepKey ? null : hrefFor(progress.nextStepKey);

  return (
    <Box>
      <CapPageHeader
        backLabel={t('journey.back')}
        onBack={() => navigate(`/academic/programs/${programId}`)}
        title={t('journey.title')}
        subtitle={programName}
        right={
          <Button
            startIcon={<RefreshRounded />}
            onClick={refresh}
            sx={{ textTransform: 'none', color: 'text.secondary' }}
          >
            {t('journey.refresh')}
          </Button>
        }
      />

      <Box sx={{ px: 3, py: 3, pb: 6, maxWidth: CAP_CONTENT_MAX_WIDTH, mx: 'auto' }}>
        <Typography variant="body1" color="text.secondary" mb={3}>
          {t('journey.subtitle')}
        </Typography>

        {/* La decisión de TMAI va primero: es el hecho que cambia el sentido de
            todo lo que sigue. El porcentaje de abajo mide preparación para
            someter, no cumplimiento — y con esto a la vista deja de leerse como
            trabajo pendiente. */}
        {hasCapTarget === false && (
          <Alert severity="info" sx={{ mb: 3 }}>
            {t('journey.noCapTarget')}
          </Alert>
        )}

        {certified && certification?.grantedAt && (
          // Otorgada CON CONDICIONES se dice distinto y en otro color: el
          // coordinador tiene que saber que le queda algo por hacer y para
          // cuándo, o la condición es sólo una nota interna de TMAI.
          <Alert severity={certification.conditions ? 'warning' : 'success'} sx={{ mb: 3 }}>
            {t(
              certification.conditions
                ? 'journey.certifiedWithConditionsBanner'
                : 'journey.certifiedBanner',
              {
                cap: certification.capCode,
                date: formatDate(certification.grantedAt, { dateStyle: 'long' }),
                reviewBy: certification.conditions
                  ? formatDate(certification.conditions.reviewBy, { dateStyle: 'long' })
                  : '',
                n: certification.conditions?.items.length ?? 0,
              },
            )}
          </Alert>
        )}

        {loading && !progress ? (
          <Stack alignItems="center" py={10} spacing={2}>
            <CircularProgress />
            <Typography color="text.secondary">{t('journey.loading')}</Typography>
          </Stack>
        ) : error && !progress ? (
          <Alert severity="warning">{t('journey.error')}</Alert>
        ) : progress ? (
          <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3} alignItems="flex-start">
            {/* ── Left rail: progress + audit status + config ─────────── */}
            <Box
              sx={{
                width: { xs: '100%', lg: 380 },
                flexShrink: 0,
                position: { lg: 'sticky' },
                top: { lg: 96 },
              }}
            >
              <Stack spacing={2.5}>
                {/* Progress hero */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 4,
                    color: '#fff',
                    background: 'linear-gradient(140deg, #4f46e5 0%, #7c3aed 100%)',
                    boxShadow: '0 14px 34px -16px rgba(79,70,229,0.55)',
                  }}
                >
                  <Typography variant="overline" sx={{ opacity: 0.85, letterSpacing: 1 }}>
                    {t('journey.progressTitle')}
                  </Typography>
                  <Stack direction="row" alignItems="baseline" spacing={1}>
                    <Typography variant="h2" fontWeight={800} lineHeight={1}>
                      {progress.pct}
                    </Typography>
                    <Typography variant="h5" fontWeight={700} sx={{ opacity: 0.9 }}>
                      %
                    </Typography>
                  </Stack>
                  <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                    {t('journey.stepsDone', {
                      done: progress.doneCount,
                      total: progress.totalCount,
                    })}
                  </Typography>

                  <LinearProgress
                    variant="determinate"
                    value={progress.pct}
                    sx={{
                      my: 2,
                      height: 8,
                      borderRadius: 4,
                      bgcolor: 'rgba(255,255,255,0.25)',
                      '& .MuiLinearProgress-bar': { bgcolor: '#fff', borderRadius: 4 },
                    }}
                  />

                  {/* Compliance status pill — only while steps remain; once every
                      step is done the final line below carries the status (no dupe).
                      Frosted so it harmonizes on the gradient. */}
                  {latestSnapshot && nextHref && (
                    <Chip
                      icon={compliant ? <CheckCircleRounded /> : <WarningAmberRounded />}
                      label={t('journey.auditStatus', {
                        status: t(`compliance.status.${latestSnapshot.overallStatus}`),
                      })}
                      sx={{
                        width: '100%',
                        justifyContent: 'flex-start',
                        bgcolor: 'rgba(255,255,255,0.14)',
                        border: '1px solid rgba(255,255,255,0.22)',
                        color: '#fff',
                        fontWeight: 700,
                        '& .MuiChip-icon': { color: compliant ? '#6ee7b7' : '#fcd34d' },
                      }}
                    />
                  )}

                  {nextHref ? (
                    <Button
                      fullWidth
                      variant="contained"
                      endIcon={<ArrowForwardRounded />}
                      onClick={() => navigate(nextHref)}
                      sx={{
                        mt: 2,
                        bgcolor: '#fff',
                        color: '#4f46e5',
                        textTransform: 'none',
                        fontWeight: 700,
                        py: 1,
                        '&:hover': { bgcolor: '#eef2ff' },
                      }}
                    >
                      {t('journey.continue')}
                    </Button>
                  ) : (
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{ mt: 2, opacity: 0.95 }}
                    >
                      {withGaps ? (
                        <WarningAmberRounded sx={{ color: '#fcd34d' }} />
                      ) : (
                        <CheckCircleRounded sx={{ color: '#6ee7b7' }} />
                      )}
                      <Typography variant="body2" fontWeight={700}>
                        {withGaps ? t('journey.allStepsGaps') : t('journey.allDone')}
                      </Typography>
                    </Stack>
                  )}
                </Paper>

                {/* Certification config */}
                <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 4 }}>
                  <Typography
                    variant="overline"
                    color="text.secondary"
                    sx={{ letterSpacing: 1, fontWeight: 700 }}
                  >
                    {t('journey.configTitle')}
                  </Typography>
                  <Stack spacing={2} mt={1.5}>
                    <TextField
                      select
                      size="small"
                      fullWidth
                      label={t('journey.cap')}
                      value={capCode}
                      onChange={(e) => setCapCode(e.target.value as CapCode)}
                    >
                      {CAP_CODES.map((c) => (
                        <MenuItem key={c} value={c}>
                          {t(`plos.caps.${c}`)}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      select
                      size="small"
                      fullWidth
                      label={t('journey.level')}
                      value={level}
                      onChange={(e) => setLevel(e.target.value as AcademicLevel)}
                    >
                      {LEVELS.map((l) => (
                        <MenuItem key={l} value={l}>
                          {t(`levels.${l}`)}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Stack>
                </Paper>
              </Stack>
            </Box>

            {/* ── Right: ordered steps ────────────────────────────────── */}
            <Box flex={1} width="100%">
              <Stack spacing={2}>
                {progress.steps.map((step, i) => {
                  const isNext = !certified && progress.nextStepKey === step.key;
                  const tone = toneFor(step, isNext);
                  const accent = TONE_ACCENT[tone];
                  return (
                    <Paper
                      key={step.key}
                      variant="outlined"
                      sx={{
                        p: { xs: 2, sm: 2.5 },
                        borderRadius: 4,
                        borderColor: isNext ? 'primary.main' : 'divider',
                        borderWidth: isNext ? 2 : 1,
                        borderLeft: '5px solid',
                        borderLeftColor: accent,
                        transition: 'border-color .15s, box-shadow .15s',
                        '&:hover': { boxShadow: 3 },
                      }}
                    >
                      <Stack direction="row" alignItems="flex-start" spacing={2}>
                        {/* State icon */}
                        <Box sx={{ pt: 0.25 }}>
                          {tone === 'success' ? (
                            <CheckCircleRounded color="success" />
                          ) : tone === 'warning' ? (
                            <WarningAmberRounded color="warning" />
                          ) : (
                            <RadioButtonUncheckedRounded
                              sx={{ color: isNext ? 'primary.main' : 'text.disabled' }}
                            />
                          )}
                        </Box>

                        <Box flex={1} minWidth={0}>
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}
                            flexWrap="wrap"
                            mb={0.25}
                          >
                            <Typography variant="subtitle1" fontWeight={700}>
                              <Box component="span" sx={{ color: 'text.disabled', mr: 0.75 }}>
                                {i + 1}.
                              </Box>
                              {t(`journey.steps.${step.key}.title`)}
                            </Typography>
                            {tone === 'warning' ? (
                              <Chip
                                size="small"
                                color="warning"
                                label={t('journey.doneWithGaps')}
                              />
                            ) : tone === 'success' ? (
                              <Chip size="small" color="success" label={t('journey.done')} />
                            ) : isNext ? (
                              <Chip size="small" color="primary" label={t('journey.next')} />
                            ) : (
                              <Chip size="small" variant="outlined" label={t('journey.pending')} />
                            )}
                          </Stack>
                          <Typography variant="body2" color="text.secondary">
                            {t(`journey.steps.${step.key}.desc`)}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              display: 'block',
                              mt: 0.5,
                              fontWeight: 600,
                              color: tone === 'warning' ? 'warning.main' : 'text.secondary',
                            }}
                          >
                            {detailFor(step)}
                          </Typography>
                        </Box>

                        <Button
                          variant={isNext ? 'contained' : 'outlined'}
                          size="small"
                          endIcon={<ArrowForwardRounded />}
                          onClick={() => navigate(hrefFor(step.key))}
                          sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            flexShrink: 0,
                            alignSelf: 'center',
                          }}
                        >
                          {t('journey.go')}
                        </Button>
                      </Stack>
                    </Paper>
                  );
                })}
              </Stack>
            </Box>
          </Stack>
        ) : null}
      </Box>
    </Box>
  );
}
